// The menus list, /client/:clientId/menu: one row per menu with its publish state and its website's, reached from the
// portal's burger menu only (DD-025). The list shows even with one menu, since a second one is a normal thing to add.
// ApprovalView publishes this page's crumb; a row opens the menu's page, which publishes its own trail.
import { useNavigate, useParams } from "react-router-dom";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { httpStatusOf } from "@/core/http/HttpError";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";
import { TONE_CLASS, menuStatus, type StatusLine } from "../format";
import { useMenus } from "../hooks/useMenu";

/** The row's second line: each part that says something, joined with " · ". */
function StatusParts({ parts }: { parts: StatusLine[] }) {
  return (
    <p className="text-sm leading-snug">
      {parts.map((part, index) => (
        <span key={part.text}>
          {index > 0 && <span className="text-gray-300"> · </span>}
          <span className={TONE_CLASS[part.tone]}>{part.text}</span>
        </span>
      ))}
    </p>
  );
}

export function MenusRoute() {
  const { clientId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { data, isLoading, error } = useMenus();
  const menus = data?.menus ?? [];

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
        <WorkspacePanel title={t("client.menu.menu")} collapsed={false} loading={isLoading}>
          {error ? (
            <p className={cn("text-sm", httpStatusOf(error) === 404 ? "text-gray-500" : "text-red-600")}>
              {httpStatusOf(error) === 404 ? "Il menu digitale non è attivo." : "Non è stato possibile caricare i menu."}
            </p>
          ) : (
            <PanelList
              items={menus}
              keyFor={(menu) => menu.id}
              empty={<p className="text-sm text-gray-500">Nessun menu.</p>}
              renderItem={(menu) => (
                <PanelListItem
                  className="py-3"
                  title={menu.name}
                  meta={<StatusParts parts={menuStatus(menu)} />}
                  onPress={() => navigate(`/client/${clientId}/menu/${encodeURIComponent(menu.id)}`)}
                />
              )}
            />
          )}
        </WorkspacePanel>
      </div>
    </div>
  );
}
